import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Phone, Play, Pause, Clock, CheckCircle, XCircle, ArrowRightLeft, BarChart3, Filter, X, LinkIcon, Plus, User } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';
import { is } from 'date-fns/locale';
import { useAuthStore } from '@/stores/auth';
import { calls, leads, type Lead } from '@/lib/api';
import { formatDateTime, formatDuration, formatPhone, statusLabels, cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

interface CallFilters {
  dateFrom: string;
  dateTo: string;
  outcome: string;
}

const outcomeOptions = [
  { value: '', label: 'Allar niðurstöður' },
  { value: 'skodun_bokud', label: 'Skoðun bókuð' },
  { value: 'upplysingar_veittar', label: 'Upplýsingar veittar' },
  { value: 'endurhringing_oskad', label: 'Endurhringing óskað' },
  { value: 'rusl', label: 'Rusl' },
];

// Map Icelandic outcome values to icons
const outcomeIconsIcelandic: Record<string, React.ReactNode> = {
  skodun_bokud: <CheckCircle className="w-4 h-4 text-green-500" />,
  upplysingar_veittar: <CheckCircle className="w-4 h-4 text-blue-500" />,
  endurhringing_oskad: <Clock className="w-4 h-4 text-yellow-500" />,
  rusl: <XCircle className="w-4 h-4 text-red-500" />,
};

const outcomeIcons: Record<string, React.ReactNode> = {
  lead_captured: <CheckCircle className="w-4 h-4 text-green-500" />,
  viewing_booked: <CheckCircle className="w-4 h-4 text-green-500" />,
  transferred: <ArrowRightLeft className="w-4 h-4 text-blue-500" />,
  info_provided: <CheckCircle className="w-4 h-4 text-blue-500" />,
  missed: <XCircle className="w-4 h-4 text-red-500" />,
  voicemail: <Clock className="w-4 h-4 text-yellow-500" />,
};

export default function Calls() {
  const { token } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [filters, setFilters] = useState<CallFilters>({
    dateFrom: '',
    dateTo: '',
    outcome: '',
  });
  const [page, setPage] = useState(1);
  const limit = 50;

  // State for linking to lead
  const [_linkingCallId, setLinkingCallId] = useState<string | null>(null);
  const [showNewLeadDialog, setShowNewLeadDialog] = useState(false);
  const [newLeadData, setNewLeadData] = useState({
    caller_name: '',
    caller_email: '',
    notes: '',
  });
  const [selectedCallForNewLead, setSelectedCallForNewLead] = useState<{
    id: string;
    caller_phone: string;
  } | null>(null);

  // Build query params from filters
  const queryParams = useMemo(() => {
    const params: { limit: number; offset: number; outcome?: string; date_from?: string; date_to?: string } = {
      limit,
      offset: (page - 1) * limit,
    };
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    return params;
  }, [filters, page, limit]);

  const { data, isLoading } = useQuery({
    queryKey: ['calls', queryParams],
    queryFn: () => calls.list(token!, queryParams),
    enabled: !!token,
  });

  // Fetch all leads for linking dropdown
  const { data: leadsData } = useQuery({
    queryKey: ['leads-all'],
    queryFn: () => leads.list(token!, { limit: 100 }),
    enabled: !!token,
  });

  // Create a map of lead_id to lead for quick lookup
  const leadsMap = useMemo(() => {
    const map = new Map<string, Lead>();
    leadsData?.data.forEach(lead => {
      map.set(lead.id, lead);
    });
    return map;
  }, [leadsData]);

  // Mutation for linking call to lead
  const linkToLeadMutation = useMutation({
    mutationFn: ({ callId, leadId }: { callId: string; leadId: string | null }) =>
      calls.update(token!, callId, { lead_id: leadId ?? undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls'] });
      toast({
        title: 'Tengt við ábendingu',
        description: 'Símtal hefur verið tengt við ábendingu.',
      });
      setLinkingCallId(null);
    },
    onError: () => {
      toast({
        title: 'Villa',
        description: 'Ekki tókst að tengja símtal við ábendingu.',
        variant: 'destructive',
      });
    },
  });

  // Mutation for creating new lead from call
  const createLeadMutation = useMutation({
    mutationFn: (data: Partial<Lead>) => leads.create(token!, data),
    onSuccess: (newLead) => {
      // Link the call to the newly created lead
      if (selectedCallForNewLead) {
        linkToLeadMutation.mutate({
          callId: selectedCallForNewLead.id,
          leadId: newLead.id,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['leads-all'] });
      setShowNewLeadDialog(false);
      setSelectedCallForNewLead(null);
      setNewLeadData({ caller_name: '', caller_email: '', notes: '' });
      toast({
        title: 'Ábending stofnuð',
        description: 'Ný ábending hefur verið stofnuð og tengd við símtalið.',
      });
    },
    onError: () => {
      toast({
        title: 'Villa',
        description: 'Ekki tókst að stofna ábendingu.',
        variant: 'destructive',
      });
    },
  });

  // Reset pagination when filters change
  const handleFilterChange = (key: keyof CallFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ dateFrom: '', dateTo: '', outcome: '' });
    setPage(1);
  };

  const hasActiveFilters = filters.dateFrom || filters.dateTo || filters.outcome;

  const { data: stats } = useQuery({
    queryKey: ['calls-stats'],
    queryFn: () => calls.stats(token!),
    enabled: !!token,
  });

  const { data: dailyData, isLoading: dailyLoading } = useQuery({
    queryKey: ['calls-daily'],
    queryFn: () => calls.daily(token!, 7),
    enabled: !!token,
  });

  // Format daily data for the chart with Icelandic day names
  const chartData = dailyData?.map((item) => ({
    date: item.date,
    count: item.count,
    dayName: format(parseISO(item.date), 'EEEE', { locale: is }),
    shortDay: format(parseISO(item.date), 'EEE', { locale: is }),
  })) || [];

  const handlePlayPause = (callId: string) => {
    if (playingId === callId) {
      setPlayingId(null);
    } else {
      setPlayingId(callId);
    }
  };

  const handleLinkToLead = (callId: string, leadId: string) => {
    linkToLeadMutation.mutate({ callId, leadId });
  };

  const handleUnlinkLead = (callId: string) => {
    linkToLeadMutation.mutate({ callId, leadId: null });
  };

  const handleCreateNewLead = (callId: string, callerPhone: string) => {
    setSelectedCallForNewLead({ id: callId, caller_phone: callerPhone });
    setShowNewLeadDialog(true);
  };

  const submitNewLead = () => {
    if (!selectedCallForNewLead) return;

    createLeadMutation.mutate({
      caller_phone: selectedCallForNewLead.caller_phone,
      caller_name: newLeadData.caller_name || undefined,
      caller_email: newLeadData.caller_email || undefined,
      notes: newLeadData.notes || undefined,
      source: 'simtal',
      status: 'nyr',
      lead_score: 50,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Símtöl</h1>
        <p className="text-gray-600 mt-1">
          {stats?.total || 0} símtöl samtals · {stats?.today || 0} í dag
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
          <p className="text-sm text-gray-500">Samtals</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-green-600">{stats?.today || 0}</p>
          <p className="text-sm text-gray-500">Í dag</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-blue-600">
            {data?.data.filter(c => c.ai_handled).length || 0}
          </p>
          <p className="text-sm text-gray-500">AI svaraði</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-purple-600">
            {data?.data.filter(c => c.transferred_to).length || 0}
          </p>
          <p className="text-sm text-gray-500">Tengt við mann</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-5 h-5 text-gray-500" />
          <h2 className="text-sm font-medium text-gray-700">Sía</h2>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto flex items-center gap-1 text-sm text-prima-600 hover:text-prima-700"
            >
              <X className="w-4 h-4" />
              Hreinsa síu
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Date From */}
          <div>
            <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-600 mb-1">
              Frá dagsetningu
            </label>
            <input
              type="date"
              id="dateFrom"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-prima-500 focus:border-transparent"
            />
          </div>
          {/* Date To */}
          <div>
            <label htmlFor="dateTo" className="block text-sm font-medium text-gray-600 mb-1">
              Til dagsetningar
            </label>
            <input
              type="date"
              id="dateTo"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-prima-500 focus:border-transparent"
            />
          </div>
          {/* Outcome */}
          <div>
            <label htmlFor="outcome" className="block text-sm font-medium text-gray-600 mb-1">
              Niðurstaða
            </label>
            <select
              id="outcome"
              value={filters.outcome}
              onChange={(e) => handleFilterChange('outcome', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-prima-500 focus:border-transparent bg-white"
            >
              {outcomeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Daily Calls Chart */}
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-prima-600" />
          <h2 className="text-lg font-semibold text-gray-900">Símtöl síðustu 7 daga</h2>
        </div>
        {dailyLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            Engin gögn til að sýna
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="shortDay"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  formatter={(value: number) => [`${value} símtöl`, 'Fjöldi']}
                  labelFormatter={(label, payload) => {
                    if (payload && payload.length > 0) {
                      return payload[0].payload.dayName;
                    }
                    return label;
                  }}
                />
                <Bar
                  dataKey="count"
                  fill="#6366f1"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={60}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Call list */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
          </div>
        ) : data?.data.length === 0 ? (
          <div className="text-center py-12">
            <Phone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {hasActiveFilters ? 'Engin símtöl passa við síu' : 'Engin símtöl ennþá'}
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {data?.data.map((call) => {
                const linkedLead = call.lead_id ? leadsMap.get(call.lead_id) : null;

                return (
                  <div
                    key={call.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {/* Play button */}
                      {call.recording_url ? (
                        <button
                          onClick={() => handlePlayPause(call.id)}
                          className={cn(
                            'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                            playingId === call.id
                              ? 'bg-prima-100 text-prima-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          )}
                        >
                          {playingId === call.id ? (
                            <Pause className="w-5 h-5" />
                          ) : (
                            <Play className="w-5 h-5 ml-0.5" />
                          )}
                        </button>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-gray-400" />
                        </div>
                      )}

                      {/* Call info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">
                            {formatPhone(call.caller_phone)}
                          </p>
                          {call.ai_handled && (
                            <span className="badge-blue text-xs">AI</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDuration(call.duration_seconds)}
                          </span>
                          <span className="flex items-center gap-1">
                            {outcomeIcons[call.outcome] || outcomeIconsIcelandic[call.outcome] || <Phone className="w-3.5 h-3.5" />}
                            {statusLabels[call.outcome] || call.outcome}
                          </span>
                        </div>
                      </div>

                      {/* Linked Lead Display */}
                      <div className="hidden sm:flex items-center gap-2 min-w-[180px]">
                        {linkedLead ? (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm">
                              <User className="w-3.5 h-3.5" />
                              <span className="font-medium truncate max-w-[100px]">
                                {linkedLead.caller_name || formatPhone(linkedLead.caller_phone)}
                              </span>
                            </div>
                            <button
                              onClick={() => handleUnlinkLead(call.id)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              title="Aftengja ábendingu"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                                <LinkIcon className="w-3.5 h-3.5" />
                                <span>Tengja við ábendingu</span>
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                              <DropdownMenuLabel>Velja ábendingu</DropdownMenuLabel>
                              <DropdownMenuSeparator />

                              {/* Create new lead option */}
                              <DropdownMenuItem
                                onClick={() => handleCreateNewLead(call.id, call.caller_phone)}
                                className="text-prima-600 font-medium"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Stofna nýja ábendingu
                              </DropdownMenuItem>

                              <DropdownMenuSeparator />

                              {/* Existing leads */}
                              {leadsData?.data && leadsData.data.length > 0 ? (
                                <div className="max-h-48 overflow-y-auto">
                                  {leadsData.data.slice(0, 10).map((lead) => (
                                    <DropdownMenuItem
                                      key={lead.id}
                                      onClick={() => handleLinkToLead(call.id, lead.id)}
                                    >
                                      <div className="flex flex-col">
                                        <span className="font-medium">
                                          {lead.caller_name || formatPhone(lead.caller_phone)}
                                        </span>
                                        {lead.caller_name && (
                                          <span className="text-xs text-gray-500">
                                            {formatPhone(lead.caller_phone)}
                                          </span>
                                        )}
                                      </div>
                                    </DropdownMenuItem>
                                  ))}
                                </div>
                              ) : (
                                <div className="px-2 py-3 text-sm text-gray-500 text-center">
                                  Engar ábendingar til staðar
                                </div>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>

                      {/* Transcript preview */}
                      {call.transcript && (
                        <div className="hidden lg:block max-w-xs">
                          <p className="text-sm text-gray-500 truncate">
                            "{call.transcript.slice(0, 80)}..."
                          </p>
                        </div>
                      )}

                      {/* Date */}
                      <div className="text-right text-sm text-gray-500">
                        {formatDateTime(call.created_at)}
                      </div>
                    </div>

                    {/* Mobile lead linking */}
                    <div className="sm:hidden mt-3 pt-3 border-t border-gray-100">
                      {linkedLead ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm">
                            <User className="w-3.5 h-3.5" />
                            <span className="font-medium">
                              {linkedLead.caller_name || formatPhone(linkedLead.caller_phone)}
                            </span>
                          </div>
                          <button
                            onClick={() => handleUnlinkLead(call.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                            title="Aftengja ábendingu"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors w-full justify-center">
                              <LinkIcon className="w-3.5 h-3.5" />
                              <span>Tengja við ábendingu</span>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center" className="w-64">
                            <DropdownMenuLabel>Velja ábendingu</DropdownMenuLabel>
                            <DropdownMenuSeparator />

                            <DropdownMenuItem
                              onClick={() => handleCreateNewLead(call.id, call.caller_phone)}
                              className="text-prima-600 font-medium"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Stofna nýja ábendingu
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />

                            {leadsData?.data && leadsData.data.length > 0 ? (
                              <div className="max-h-48 overflow-y-auto">
                                {leadsData.data.slice(0, 10).map((lead) => (
                                  <DropdownMenuItem
                                    key={lead.id}
                                    onClick={() => handleLinkToLead(call.id, lead.id)}
                                  >
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        {lead.caller_name || formatPhone(lead.caller_phone)}
                                      </span>
                                      {lead.caller_name && (
                                        <span className="text-xs text-gray-500">
                                          {formatPhone(lead.caller_phone)}
                                        </span>
                                      )}
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                              </div>
                            ) : (
                              <div className="px-2 py-3 text-sm text-gray-500 text-center">
                                Engar ábendingar til staðar
                              </div>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    {/* Expanded audio player */}
                    {playingId === call.id && call.recording_url && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <audio
                          src={calls.getRecordingUrl(token!, call.id)}
                          controls
                          autoPlay
                          className="w-full"
                          onEnded={() => setPlayingId(null)}
                        />
                        {call.transcript && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 mb-1">Umritun:</p>
                            <p className="text-sm text-gray-600">{call.transcript}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {data && data.pagination.total > limit && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Sýni {((page - 1) * limit) + 1}-{Math.min(page * limit, data.pagination.total)} af {data.pagination.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={cn(
                      'px-3 py-1.5 text-sm rounded-md transition-colors',
                      page === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    )}
                  >
                    Fyrri
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page * limit >= data.pagination.total}
                    className={cn(
                      'px-3 py-1.5 text-sm rounded-md transition-colors',
                      page * limit >= data.pagination.total
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    )}
                  >
                    Næsti
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* New Lead Dialog */}
      <Dialog open={showNewLeadDialog} onOpenChange={setShowNewLeadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stofna nýja ábendingu</DialogTitle>
            <DialogDescription>
              Stofnaðu nýja ábendingu úr þessu símtali. Símanúmerið verður sjálfkrafa tengt.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Phone (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Símanúmer
              </label>
              <input
                type="text"
                value={selectedCallForNewLead ? formatPhone(selectedCallForNewLead.caller_phone) : ''}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500"
              />
            </div>

            {/* Name */}
            <div>
              <label htmlFor="newLeadName" className="block text-sm font-medium text-gray-700 mb-1">
                Nafn
              </label>
              <input
                type="text"
                id="newLeadName"
                value={newLeadData.caller_name}
                onChange={(e) => setNewLeadData(prev => ({ ...prev, caller_name: e.target.value }))}
                placeholder="Nafn viðskiptavinar"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-prima-500 focus:border-transparent"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="newLeadEmail" className="block text-sm font-medium text-gray-700 mb-1">
                Netfang
              </label>
              <input
                type="email"
                id="newLeadEmail"
                value={newLeadData.caller_email}
                onChange={(e) => setNewLeadData(prev => ({ ...prev, caller_email: e.target.value }))}
                placeholder="netfang@dæmi.is"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-prima-500 focus:border-transparent"
              />
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="newLeadNotes" className="block text-sm font-medium text-gray-700 mb-1">
                Athugasemdir
              </label>
              <textarea
                id="newLeadNotes"
                value={newLeadData.notes}
                onChange={(e) => setNewLeadData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Athugasemdir um viðskiptavininn..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-prima-500 focus:border-transparent resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setShowNewLeadDialog(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Hætta við
            </button>
            <button
              onClick={submitNewLead}
              disabled={createLeadMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-prima-600 rounded-lg hover:bg-prima-700 transition-colors disabled:opacity-50"
            >
              {createLeadMutation.isPending ? 'Vista...' : 'Stofna ábendingu'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
